/**
 * PlaybookCard (DEV-1278) — catalog card for a published playbook,
 * Variation A (Skip, 2026-06-09): a flip card. FRONT answers the adopter's
 * questions at a glance — hero (master photo, category-color fallback),
 * category, title, the headline outcome, and cost / typical duration /
 * minimum people. BACK holds the description and "View master". "Use this
 * playbook" stays on the front; a flip control swaps faces.
 *
 * Accessibility: both faces are in the DOM, so the hidden face is marked
 * aria-hidden and its controls are taken out of the tab order; focus moves
 * to the newly-shown face's primary control on flip.
 *
 * Summary fields read from cheap stored columns on the playbook — no
 * published_content parsing. Every stat hides when its value is null.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { MiniProjectPlaybook } from "../../api/miniProjects";
import { mediaSrc } from "../../api/mediaUrl";
import styles from "./PlaybookCard.module.css";

interface Props {
  playbook: MiniProjectPlaybook;
  onUse: (playbook: MiniProjectPlaybook) => void;
  onViewMaster: (masterMiniProjectGuid: string) => void;
  /** "View master" is an authoring affordance pointing at the live, possibly
   *  mid-edit master — shown only to the playbook owner / community admin
   *  (DEV-1278). Consumers never see it. Defaults to hidden. */
  showViewMaster?: boolean;
}

export default function PlaybookCard({ playbook, onUse, onViewMaster, showViewMaster = false }: Props) {
  const { t } = useAppTranslation("pages");
  const [flipped, setFlipped] = useState(false);
  const useBtnRef = useRef<HTMLButtonElement>(null);
  const backBtnRef = useRef<HTMLButtonElement>(null);
  const didMount = useRef(false);

  // Move focus to the newly-revealed face on flip (skip the first render).
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    (flipped ? backBtnRef : useBtnRef).current?.focus();
  }, [flipped]);

  const cost = useMemo(() => {
    if (playbook.plannedBudgetAmount == null) return null;
    const currency = playbook.plannedBudgetCurrency || "USD";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(playbook.plannedBudgetAmount);
    } catch {
      return `${Math.round(playbook.plannedBudgetAmount)} ${currency}`;
    }
  }, [playbook.plannedBudgetAmount, playbook.plannedBudgetCurrency]);

  const duration = useMemo(() => {
    const d = playbook.typicalDurationDays;
    if (d == null || d <= 0) return null;
    if (d < 10) return t("playbookCard.durDays", { count: d });
    if (d < 70) return t("playbookCard.durWeeks", { count: Math.round(d / 7) });
    return t("playbookCard.durMonths", { count: Math.round(d / 30) });
  }, [playbook.typicalDurationDays, t]);

  // Full outcome list for the back face — parsed from published_content's
  // mission KPIs (DEV-1276). Composed the same way as the stored primary
  // outcome: "<name>: <target> <uom>". Empty for legacy/pre-fix playbooks.
  const outcomes = useMemo(() => {
    if (!playbook.publishedContent) return [] as string[];
    try {
      const parsed = JSON.parse(playbook.publishedContent);
      const mk = Array.isArray(parsed?.missionKpis) ? parsed.missionKpis : [];
      return mk
        .map((g: any) => {
          const name = String(g?.displayName ?? "").trim() || "Goal";
          const target = g?.targetGoal;
          if (target == null) return name;
          const t = Number.isInteger(target) ? String(target) : String(target);
          const uom = g?.uom ? ` ${String(g.uom).trim()}` : "";
          return `${name}: ${t}${uom}`;
        })
        .filter(Boolean) as string[];
    } catch {
      return [] as string[];
    }
  }, [playbook.publishedContent]);

  const hasMetrics = cost != null || duration != null || playbook.minVolunteers != null;
  const frontHidden = flipped;
  const backHidden = !flipped;

  const flip = useCallback(() => setFlipped((f) => !f), []);

  return (
    <div className={styles.card}>
      <div className={`${styles.inner} ${flipped ? styles.flipped : ""}`}>

        {/* ── Front ── the content area itself is the flip trigger (click or
            keyboard); "Use this playbook" is a sibling so it never flips and
            isn't an interactive child of a button. */}
        <div className={styles.front} aria-hidden={frontHidden}>
          <div
            className={styles.flipFace}
            role="button"
            tabIndex={frontHidden ? -1 : 0}
            aria-label={t("playbookCard.flipToDetails")}
            onClick={flip}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); flip(); }
            }}
          >
            <div className={styles.hero}>
              {playbook.heroImageUrl ? (
                <img className={styles.heroImg} src={mediaSrc(playbook.heroImageUrl) ?? undefined} alt="" />
              ) : (
                <span className={styles.heroFallback} aria-hidden="true">▦</span>
              )}
            </div>

            <div className={styles.body}>
              {playbook.categoryKey && <span className={styles.category}>{playbook.categoryKey}</span>}
              <div className={styles.title}>{playbook.title}</div>

              {playbook.primaryOutcome && (
                <div className={styles.outcome}>
                  <span className={styles.outcomeIcon} aria-hidden="true">◎</span>
                  <span className={styles.outcomeText}>
                    {playbook.primaryOutcome}
                    {playbook.goalCount && playbook.goalCount > 1 ? (
                      <span className={styles.outcomeMore}>
                        {" "}· {t("playbookCard.moreGoals", { count: playbook.goalCount - 1 })}
                      </span>
                    ) : null}
                  </span>
                </div>
              )}

              {hasMetrics && (
                <div className={styles.metrics}>
                  <div className={styles.metric}>
                    <span className={styles.metricLabel}>{t("playbookCard.cost")}</span>
                    <span className={styles.metricValue}>{cost ?? "—"}</span>
                  </div>
                  <div className={styles.metric}>
                    <span className={styles.metricLabel}>{t("playbookCard.time")}</span>
                    <span className={styles.metricValue}>{duration ?? "—"}</span>
                  </div>
                  <div className={styles.metric}>
                    <span className={styles.metricLabel}>{t("playbookCard.minPeople")}</span>
                    <span className={styles.metricValue}>
                      {playbook.minVolunteers != null ? playbook.minVolunteers : "—"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.actionsBar}>
            <button
              ref={useBtnRef}
              className={styles.btnPrimary}
              onClick={() => onUse(playbook)}
              tabIndex={frontHidden ? -1 : 0}
              style={{ flex: 1 }}
            >
              {t("playbookCard.use")}
            </button>
          </div>
        </div>

        {/* ── Back ── (click anywhere except the action buttons flips) */}
        <div className={`${styles.front} ${styles.back}`} aria-hidden={backHidden} onClick={flip}>
          <div className={styles.backBody}>
            <div className={styles.backTitle}>{playbook.title}</div>

            {outcomes.length > 0 && (
              <div className={styles.outcomeList}>
                <div className={styles.outcomeHead}>{t("playbookCard.outcomes")}</div>
                {outcomes.map((o, i) => (
                  <div key={i} className={styles.outcomeRow}>
                    <span className={styles.outcomeIcon} aria-hidden="true">◎</span>
                    <span>{o}</span>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.description}>
              {playbook.description?.trim()
                ? playbook.description
                : t("playbookCard.noDescription")}
            </div>

            <div className={styles.actions}>
              {showViewMaster && playbook.masterMiniProjectGuid && (
                <button
                  className={styles.btnGhost}
                  onClick={(e) => { e.stopPropagation(); onViewMaster(playbook.masterMiniProjectGuid as string); }}
                  tabIndex={backHidden ? -1 : 0}
                  style={{ flex: 1 }}
                >
                  {t("playbookCard.viewMaster")}
                </button>
              )}
              <button
                ref={backBtnRef}
                className={styles.flipBtn}
                onClick={(e) => { e.stopPropagation(); flip(); }}
                aria-label={t("playbookCard.flipBack")}
                tabIndex={backHidden ? -1 : 0}
              >
                ⟲
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
